export function resolveCountryCode(countryCode: string): string {
  switch (countryCode) {
    case 'at':
    case 'ch':
    case 'de':
    case 'fr-ch':
      return 'DE'
    case 'fi':
      return 'FI'
    case 'be':
    case 'nl':
      return 'NL'
    case 'fr-be':
    case 'fr':
      return 'FR'
    default:
      return 'DE'
  }
}

/**
 * Product function icons are usually passed through "as-is" (after stripping the file extension).
 * For shoe widths ("Y-Weite"), the backend delivers values like "F-Weite" that must map to the
 * dedicated webshop width icons.
 */
export function resolveFunctionIconSymbol(symbol: string): string {
  const trimmed = symbol.trim()
  const match = trimmed.match(/^([FGHKM])[-\s]?weite$/i)
  const width = match?.[1]
  if (!width) {
    return trimmed
  }
  return `webshop_weiten_${width.toLowerCase()}`
}

/**
 * List of all available function icon asset names (without the `function-icons/` prefix).
 * These names correspond to the SVGs used by `components/ui/icons/FunctionIcon.vue`.
 */
export const FUNCTION_ICON_ASSET_NAMES = [
  'default',
  '_2-wege-reissverschluss',
  '_2-wege-stretch',
  '_3-in-1',
  '_4-wege-stretch',
  '_100prozent-alpaka',
  '_100prozent-baumwolle',
  '_100prozent-kaschmir',
  '_100prozent-merino',
  '_100prozent-wolle',
  'allwettertauglich',
  'atmungsaktiv',
  'auch-fuer-prothesen-geeignet',
  'bei-leichter-inkontinenz',
  'bei-mittlerer-inkontinenz',
  'bei-starker-inkontinenz',
  'buegelfrei',
  'buegellos',
  'chlorresistent',
  'doppelpack',
  'doppelverschluss',
  'echtleder',
  'empfehlenswert-bei-langem-sitzen',
  'federleicht',
  'feuchtigkeitsregulierend',
  'formbestaendig',
  'formbund',
  'fuer-jede-jahreszeit',
  'fussspitze-handkettelt',
  'gefuetterte-pads',
  'herausnehmbare-pads',
  'kapuze-abtrennbar',
  'komfort-traeger-weite-traeger',
  'krempelbar',
  'leicht-waermend',
  'mehrfachpack',
  'minimizer',
  'mit-alpaka',
  'mit-baumwolle',
  'mit-biobaumwolle',
  'mit-buegel',
  'mit-kaschmir',
  'mit-merino',
  'mit-recyceltem-rohstoff',
  'mit-schurwolle',
  'mit-wolle',
  'modal',
  'nachhaltig',
  'nahtlos',
  'nicht-sichtbar',
  'pflegeleicht',
  'reflektierend',
  'rundum-dehnbund',
  'schnelltrocknend',
  'shaping',
  'soft-touch',
  'stark-waermend',
  'stuetzend-und-durchblutungsfoerdernd',
  'teildehnbund',
  'thermo',
  'trocknergeeignet',
  'twinset',
  'uv-schutz',
  'verdeckter-reissverschluss',
  'verstaerkte-bauchpartie',
  'verstellbare-traeger',
  'viskose',
  'vorderverschluss',
  'waermend',
  'waermeregulierend',
  'waschmaschinengeeignet',
  'wasser-abweisend',
  'wendejacke',
  'windabweisend',
  'zusaetzliche-taschen',
  'zweiteiler-v1',
  'zweiteiler-v2',
  'webshop_weiten_f',
  'webshop_weiten_g',
  'webshop_weiten_h',
  'webshop_weiten_k',
  'webshop_weiten_m',
] as const

export function resolveFunctionIconAssetName(symbol: string): string {
  const trimmed = symbol.trim()
  const startingWithNumber = /^\d/.test(trimmed)
  const prefix = startingWithNumber ? '_' : ''
  return (prefix + trimmed.toLocaleLowerCase()).trim()
}

export function hasFunctionIconAsset(symbol: string): boolean {
  const assetName = resolveFunctionIconAssetName(symbol)
  return (FUNCTION_ICON_ASSET_NAMES as readonly string[]).includes(assetName)
}
